import { ReactQueryStaleTime } from '@openx/constants/react-query';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  OptionsByPathDocument,
  type OptionsByPathQuery,
  type OptionsByPathQueryVariables,
} from '../graphql/query/options/OptionsByPath.generated';
import { getGraphQLRequestFromClient } from '../modules/graphql/types';

export type { OptionsByPathQuery, OptionsByPathQueryVariables } from '../graphql/query/options/OptionsByPath.generated';

type OptionsByPathQueryOptions =
  | {
      fetchPolicy?: 'cache-and-network' | 'cache-first' | 'network-only' | 'no-cache' | string;
      notifyOnNetworkStatusChange?: boolean;
      skip?: boolean;
      variables: OptionsByPathQueryVariables;
    }
  | {
      fetchPolicy?: 'cache-and-network' | 'cache-first' | 'network-only' | 'no-cache' | string;
      notifyOnNetworkStatusChange?: boolean;
      skip: true;
      variables?: OptionsByPathQueryVariables;
    };

export type OptionsByPathRefetchResult = {
  data?: OptionsByPathQuery;
};

export type OptionsByPathQueryHookResult = {
  data?: OptionsByPathQuery;
  error: Error | null;
  loading: boolean;
  networkStatus: number;
  refetch: () => Promise<OptionsByPathRefetchResult>;
};

export const optionsByPathQueryKey = (variables?: OptionsByPathQueryVariables) =>
  [QueryKeys.OPTIONS_LIST, 'OptionsByPath', variables] as const;

const getStaleTime = (fetchPolicy?: string) => {
  switch (fetchPolicy) {
    case 'cache-and-network':
    case 'network-only':
    case 'no-cache':
      return 0;
    default:
      return ReactQueryStaleTime.MINUTES_15;
  }
};

export function useOptionsByPathQuery(baseOptions: OptionsByPathQueryOptions): OptionsByPathQueryHookResult {
  const { fetchPolicy, notifyOnNetworkStatusChange, skip, variables } = baseOptions;

  const result = useQuery({
    enabled: !skip && !!variables,
    queryFn: ({ client }) => {
      if (!variables) {
        throw new Error('OptionsByPath query variables are required');
      }

      return getGraphQLRequestFromClient(client)(OptionsByPathDocument, variables);
    },
    queryKey: optionsByPathQueryKey(variables),
    staleTime: getStaleTime(fetchPolicy),
  });

  const loading = result.isLoading || (!!notifyOnNetworkStatusChange && result.isRefetching);

  const refetch = useCallback(async () => {
    const refetchResult = await result.refetch();

    return {
      data: refetchResult.data,
    };
  }, [result.refetch]);

  return {
    data: result.data,
    error: result.error,
    loading,
    networkStatus: result.isRefetching ? 4 : result.isLoading ? 1 : 7,
    refetch,
  };
}
